import { useQuery } from 'react-query';
import api from '../utils/api';

// Recommend songs for this party: rank the artists people have already
// added or suggested, search the catalog for more of their tracks, and
// offer the ones the session doesn't have yet.
const fetchPartySuggestions = async (session) => {
  // First choice: the taste blend — songs the people actually in this
  // room bring to their parties (Spotify Jam's best trick). Falls back
  // to artist-frequency picks when the room has no taste history yet.
  const blendPicks = await api
    .get(`/api/sessions/${session.id}/party-picks`)
    .then((r) => (r.data.picks || []).slice(0, 3))
    .catch(() => []);
  if (blendPicks.length >= 3) return blendPicks;

  const existing = [
    ...Object.values(session.playlist || {}),
    ...Object.values(session.suggestions || {})
      .map((s) => s.song)
      .filter(Boolean)
  ];
  if (existing.length === 0) return blendPicks;

  // Rank artists by how often the party has picked them
  const artistCounts = {};
  existing.forEach((song) => {
    const artist = (song.artist || '').trim();
    if (artist) artistCounts[artist] = (artistCounts[artist] || 0) + 1;
  });
  const topArtists = Object.entries(artistCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([artist]) => artist);
  if (topArtists.length === 0) return blendPicks;

  // Everything already in the session, by id and by title+artist
  const knownIds = new Set();
  const knownTitles = new Set();
  existing.forEach((song) => {
    [song.id, song.originalId, song.appleMusicId].forEach((id) => id && knownIds.add(id));
    knownTitles.add(`${(song.name || '').toLowerCase()}|${(song.artist || '').toLowerCase()}`);
  });

  const perArtist = await Promise.all(
    topArtists.map((artist) => {
      const params = { query: artist, musicService: session.musicService, limit: 5 };
      if (session.musicService === 'spotify') params.sessionId = session.id;
      return api
        .get('/api/playlist/search', { params })
        .then((r) => r.data.results || [])
        .catch(() => []);
    })
  );

  // Interleave one pick per artist for variety, skipping known songs
  const picks = [...blendPicks];
  const longest = Math.max(0, ...perArtist.map((r) => r.length));
  for (let i = 0; i < longest && picks.length < 3; i++) {
    for (const list of perArtist) {
      const song = list[i];
      if (!song) continue;
      const titleKey = `${(song.name || '').toLowerCase()}|${(song.artist || '').toLowerCase()}`;
      if (knownIds.has(song.id) || knownTitles.has(titleKey)) continue;
      if (picks.some((p) => p.id === song.id)) continue;
      picks.push(song);
      if (picks.length >= 3) break;
    }
  }
  return picks;
};

export const usePartySuggestions = (session, enabled = true) => {
  const historyCount =
    Object.keys(session?.playlist || {}).length +
    Object.keys(session?.suggestions || {}).length;

  return useQuery(
    ['partySuggestions', session?.id, session?.musicService, historyCount],
    () => fetchPartySuggestions(session),
    {
      // The taste blend works even before the first song is queued
      enabled: enabled && !!session?.id,
      staleTime: 60 * 1000,
      cacheTime: 5 * 60 * 1000,
      retry: 0
    }
  );
};
